import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
  Box, Card, CardContent, CardHeader,
  Typography, Divider, Grid, Button,
  IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead,
  TableRow, Paper, Select, MenuItem,
  Chip, Stack
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
  collection, getDocs, addDoc,
  updateDoc, deleteDoc, doc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import Swal from "sweetalert2";

function Roomtarrifs() {
  const auth = getAuth();

  const [userId, setUserId] = useState(null);
  const [propertyId, setPropertyId] = useState(null);

  const [rooms, setRooms] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tariffs, setTariffs] = useState([]);

  const [form, setForm] = useState({
    roomId: "",
    ratePlanId: "",
    status: "active"
  });

  const [editId, setEditId] = useState(null);

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        const snap = await getDocs(collection(db, "authroles"));
        snap.forEach(d => {
          if (d.id === user.uid) {
            setPropertyId(d.data().propertyId);
          }
        });
      }
    });
    return () => unsub();
  }, []);

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    if (userId && propertyId) fetchAll();
  }, [userId, propertyId]);

  const fetchAll = async () => {
    const roomSnap = await getDocs(collection(db, "rooms"));
    setRooms(
      roomSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(r => r.propertyId === propertyId)
    );

    const planSnap = await getDocs(collection(db, "rate_plans"));
    setPlans(
      planSnap.docs.map(d => ({ id: d.id, ...d.data() }))
    );

    const tariffSnap = await getDocs(collection(db, "room_tariffs"));
    setTariffs(
      tariffSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(t => t.propertyId === propertyId)
    );
  };

  /* ================= SAVE ================= */
  const saveTariff = async () => {
    if (!form.roomId || !form.ratePlanId) {
      return Swal.fire("Required", "Room & Rate Plan are required", "warning");
    }

    if (editId) {
      await updateDoc(doc(db, "room_tariffs", editId), form);
      Swal.fire("Updated", "Room plan updated", "success");
    } else {
      await addDoc(collection(db, "room_tariffs"), {
        ...form,
        userId,
        propertyId,
        createdAt: new Date()
      });
      Swal.fire("Added", "Room plan added", "success");
    }

    resetForm();
    fetchAll();
  };

  /* ================= EDIT ================= */
  const editTariff = (row) => {
    setForm({
      roomId: row.roomId,
      ratePlanId: row.ratePlanId,
      status: row.status
    });
    setEditId(row.id);
  };

  /* ================= DELETE ================= */
  const deleteTariff = async (id) => {
    const ok = await Swal.fire({
      title: "Delete mapping?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete"
    });

    if (ok.isConfirmed) {
      await deleteDoc(doc(db, "room_tariffs", id));
      Swal.fire("Deleted", "Mapping removed", "success");
      fetchAll();
    }
  };

  /* ================= HELPERS ================= */
  const resetForm = () => {
    setForm({ roomId: "", ratePlanId: "", status: "active" });
    setEditId(null);
  };

  /* ================= GROUP BY ROOM ================= */
  const grouped = rooms.map(room => ({
    ...room,
    plans: tariffs.filter(t => t.roomId === room.id)
  }));

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
      <Menu />

      <Box flex={1} p={3} className="content-wrapper">
        <Typography variant="h5" fontWeight={800} mb={3}>
          Room – Rate Plan Mapping
        </Typography>

        {/* ================= FORM ================= */}
        <Card sx={{ mb: 4, borderRadius: 4 }}>
          <CardHeader title={editId ? "Edit Mapping" : "Add Room Plan"} />
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  size="small"
                  value={form.roomId}
                  onChange={(e) => setForm({ ...form, roomId: e.target.value })}
                  displayEmpty
                >
                  <MenuItem value="">Select Room</MenuItem>
                  {rooms.map(r => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.roomTypeName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  size="small"
                  value={form.ratePlanId}
                  onChange={(e) => setForm({ ...form, ratePlanId: e.target.value })}
                  displayEmpty
                >
                  <MenuItem value="">Select Plan</MenuItem>
                  {plans.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.planCode} - {p.planName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} md={2}>
                <Select
                  fullWidth
                  size="small"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  startIcon={<Add />}
                  onClick={saveTariff}
                >
                  {editId ? "Update" : "Add"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ================= TABLE ================= */}
        <Card sx={{ borderRadius: 4 }}>
          <CardHeader title="Room Plans" />
          <Divider />
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Plans</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {grouped.map((room, idx) => (
                  <TableRow key={room.id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell fontWeight={600}>
                      {room.roomTypeName}
                    </TableCell>

                    <TableCell>
                      <Stack spacing={1}>
                        {room.plans.length ? room.plans.map(p => {
                          const planName =
                            plans.find(pl => pl.id === p.ratePlanId)?.planName || "-";
                          return (
                            <Paper
                              key={p.id}
                              sx={{
                                p: 1,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderRadius: 2
                              }}
                            >
                              <Typography variant="body2">
                                {planName}
                              </Typography>
                              <Stack direction="row" spacing={0.5}>
                                <IconButton size="small" onClick={() => editTariff(p)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => deleteTariff(p.id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Paper>
                          );
                        }) : (
                          <Typography variant="body2">No plans</Typography>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      {room.plans.length ? (
                        <Chip
                          label={room.plans[0].status}
                          color={room.plans[0].status === "active" ? "success" : "default"}
                          size="small"
                        />
                      ) : (
                        <Chip label="N/A" size="small" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {!grouped.length && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No rooms found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </Box>
  );
}

export default Roomtarrifs;
